import React, { useEffect, useMemo, useState } from 'react';
import { Timestamp, deleteField, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../context/AppConfigContext';
import { useNavigate } from 'react-router-dom';
import { isAdminEmail, normalizeEmail, PRIMARY_ADMIN_EMAIL } from '../utils/admin';

export default function Admin() {
  const { user, loading } = useAuth();
  const { adminEmails: configAdminEmails } = useAppConfig();
  const navigate = useNavigate();

  const [maintenance, setMaintenance] = useState({ disabled: false, message: '', endsAt: null, initiatedBy: '' });
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState('');
  const [timerMinutes, setTimerMinutes] = useState('30');

  const [banner, setBanner] = useState({
    enabled: false,
    text: '',
    tone: 'info',
    linkUrl: '',
    linkLabel: '',
  });
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerError, setBannerError] = useState('');

  const [adminEmails, setAdminEmails] = useState([]);
  const [adminInput, setAdminInput] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState('');

  const canAccess = useMemo(() => {
    return !!user && isAdminEmail(user.email, configAdminEmails);
  }, [user, configAdminEmails]);

  useEffect(() => {
    if (!loading) {
      if (!canAccess) navigate('/login');
    }
  }, [loading, canAccess, navigate]);

  useEffect(() => {
    if (!canAccess) return;
    const ref = doc(db, 'config', 'app');
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      const nextAdminEmails = Array.isArray(data.adminEmails)
        ? data.adminEmails.filter((e) => typeof e === 'string').map((e) => e.trim()).filter(Boolean)
        : [];
      setMaintenance({
        disabled: !!data.disabled,
        message: typeof data.message === 'string' ? data.message : '',
        endsAt: data.endsAt || null,
        initiatedBy: typeof data.initiatedBy === 'string' ? data.initiatedBy : '',
      });
      setBanner({
        enabled: !!data?.banner?.enabled,
        text: typeof data?.banner?.text === 'string' ? data.banner.text : '',
        tone: typeof data?.banner?.tone === 'string' ? data.banner.tone : 'info',
        linkUrl: typeof data?.banner?.linkUrl === 'string' ? data.banner.linkUrl : '',
        linkLabel: typeof data?.banner?.linkLabel === 'string' ? data.banner.linkLabel : '',
      });
      setAdminEmails(nextAdminEmails);
    }, (err) => {
      setMaintenanceError(err?.message || 'Failed to load maintenance config');
      setBannerError(err?.message || 'Failed to load banner config');
      setAdminError(err?.message || 'Failed to load admin list');
    });
    return () => unsub();
  }, [canAccess]);

  const addAdminEmail = () => {
    const normalized = normalizeEmail(adminInput);
    if (!normalized || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) {
      setAdminError('Enter a valid email address');
      return;
    }
    const next = Array.from(new Set([
      ...adminEmails.map(normalizeEmail).filter(Boolean),
      normalized,
    ]));
    setAdminEmails(next);
    setAdminInput('');
    setAdminError('');
  };

  const removeAdminEmail = (email) => {
    const normalized = normalizeEmail(email);
    setAdminEmails((cur) => cur.filter((e) => normalizeEmail(e) !== normalized));
  };

  const saveAdminEmails = async () => {
    setAdminBusy(true);
    setAdminError('');
    try {
      const ref = doc(db, 'config', 'app');
      const payload = {
        adminEmails: adminEmails.map(normalizeEmail).filter(Boolean),
        adminUpdatedAt: serverTimestamp(),
        adminUpdatedBy: user?.email || '',
      };
      await setDoc(ref, payload, { merge: true });
    } catch (err) {
      setAdminError(err?.message || 'Failed to update admin list');
    } finally {
      setAdminBusy(false);
    }
  };

  const saveMaintenance = async (next) => {
    setMaintenanceBusy(true);
    setMaintenanceError('');
    try {
      const ref = doc(db, 'config', 'app');
      const payload = {
        disabled: !!next.disabled,
        message: typeof next.message === 'string' ? next.message : '',
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || '',
        initiatedBy: typeof next.initiatedBy === 'string' && next.initiatedBy.trim()
          ? next.initiatedBy.trim()
          : (user?.email || ''),
        endsAt: next.endsAt || deleteField(),
      };
      await setDoc(ref, payload, { merge: true });
    } catch (err) {
      setMaintenanceError(err?.message || 'Failed to update maintenance mode');
    } finally {
      setMaintenanceBusy(false);
    }
  };

  const saveBanner = async (next) => {
    setBannerBusy(true);
    setBannerError('');
    try {
      const ref = doc(db, 'config', 'app');
      const payload = {
        banner: {
          enabled: !!next.enabled,
          text: typeof next.text === 'string' ? next.text : '',
          tone: typeof next.tone === 'string' ? next.tone : 'info',
          linkUrl: typeof next.linkUrl === 'string' ? next.linkUrl : '',
          linkLabel: typeof next.linkLabel === 'string' ? next.linkLabel : '',
        },
        bannerUpdatedAt: serverTimestamp(),
        bannerUpdatedBy: user?.email || '',
      };
      await setDoc(ref, payload, { merge: true });
    } catch (err) {
      setBannerError(err?.message || 'Failed to update banner');
    } finally {
      setBannerBusy(false);
    }
  };

  const setEndsAtFromMinutes = (minutes) => {
    const m = Number(minutes);
    if (!Number.isFinite(m) || m <= 0) return;
    const d = new Date(Date.now() + m * 60 * 1000);
    setMaintenance((cur) => ({ ...cur, endsAt: Timestamp.fromDate(d), disabled: true }));
  };

  const clearEndsAt = () => {
    setMaintenance((cur) => ({ ...cur, endsAt: null }));
  };

  const endsAtLabel = (() => {
    try {
      const d = maintenance?.endsAt?.toDate?.();
      return d ? d.toLocaleString() : '';
    } catch {
      return '';
    }
  })();

  if (loading) return <div style={{ paddingTop: '4rem', textAlign: 'center' }}>Loading…</div>;
  if (!canAccess) return null;

  return (
    <div style={{ paddingTop: '6rem', display: 'flex', justifyContent: 'center' }}>
      <div className="glass-card" style={{ width: '100%', maxWidth: 1000, padding: 16 }}>
        <h2 className="card-title" style={{ marginTop: 0, textAlign: 'center' }}>Admin Panel</h2>

        <div className="glass-card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Site Banner</h3>
          {bannerError ? (
            <div style={{ color: '#ff6b6b', marginBottom: 8 }}>{String(bannerError)}</div>
          ) : null}

          <div className="form-glass" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={!!banner.enabled}
                onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
                disabled={bannerBusy}
              />
              <span>Enable banner</span>
            </label>

            <label style={{ display: 'block' }}>
              <span className="form-label">Tone</span>
              <select
                className="input-glass"
                value={banner.tone}
                onChange={(e) => setBanner({ ...banner, tone: e.target.value })}
                disabled={bannerBusy}
              >
                <option value="info">Info (blue)</option>
                <option value="success">Success (green)</option>
                <option value="warn">Warning (amber)</option>
                <option value="danger">Danger (red)</option>
              </select>
            </label>

            <textarea
              className="input-glass"
              rows={3}
              placeholder="Banner message"
              value={banner.text}
              onChange={(e) => setBanner({ ...banner, text: e.target.value })}
              disabled={bannerBusy}
            />

            <input
              className="input-glass"
              placeholder="Link label (optional)"
              value={banner.linkLabel}
              onChange={(e) => setBanner({ ...banner, linkLabel: e.target.value })}
              disabled={bannerBusy}
            />

            <input
              className="input-glass"
              placeholder="Link URL (optional)"
              value={banner.linkUrl}
              onChange={(e) => setBanner({ ...banner, linkUrl: e.target.value })}
              disabled={bannerBusy}
            />

            <div className={`site-banner site-banner-${banner.tone} site-banner-preview`} style={{ marginTop: 6 }}>
              <div className="site-banner-inner">
                <span className="site-banner-text">{banner.text || 'Banner preview text'}</span>
                {banner.linkUrl ? (
                  <span className="site-banner-link">{banner.linkLabel || 'Learn more'}</span>
                ) : null}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="btn-glass-primary"
                disabled={bannerBusy}
                onClick={() => saveBanner(banner)}
              >
                {bannerBusy ? 'Saving…' : 'Save banner'}
              </button>
              <button
                className="btn-glass-secondary"
                type="button"
                disabled={bannerBusy}
                onClick={() => setBanner({ enabled: false, text: '', tone: 'info', linkUrl: '', linkLabel: '' })}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="glass-card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Admin Access</h3>
          <div className="meta-text" style={{ marginBottom: 8 }}>
            Primary admin always has access: {PRIMARY_ADMIN_EMAIL}
          </div>
          {adminError ? (
            <div style={{ color: '#ff6b6b', marginBottom: 8 }}>{String(adminError)}</div>
          ) : null}

          <div className="form-glass" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                className="input-glass"
                placeholder="Add admin email"
                value={adminInput}
                onChange={(e) => setAdminInput(e.target.value)}
                disabled={adminBusy}
                type="email"
              />
              <button className="btn-glass-secondary" type="button" onClick={addAdminEmail} disabled={adminBusy}>
                Add
              </button>
              <button className="btn-glass-primary" type="button" onClick={saveAdminEmails} disabled={adminBusy}>
                {adminBusy ? 'Saving…' : 'Save admins'}
              </button>
            </div>

            {adminEmails.length ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {adminEmails.map((email) => (
                  <div key={email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span>{email}</span>
                    <button
                      className="nav-link"
                      type="button"
                      disabled={adminBusy}
                      onClick={() => removeAdminEmail(email)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="meta-text">No additional admins yet.</div>
            )}
          </div>
        </div>

        <div className="glass-card" style={{ padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Site Maintenance (Remote Kill Switch)</h3>
          {maintenanceError ? (
            <div style={{ color: '#ff6b6b', marginBottom: 8 }}>{String(maintenanceError)}</div>
          ) : null}

          <div className="form-glass" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={!!maintenance.disabled}
                onChange={(e) => setMaintenance({ ...maintenance, disabled: e.target.checked })}
                disabled={maintenanceBusy}
              />
              <span>Enable maintenance mode (non-admins see maintenance page)</span>
            </label>

            <input
              className="input-glass"
              placeholder="Initiated by (name or username)"
              value={maintenance.initiatedBy}
              onChange={(e) => setMaintenance({ ...maintenance, initiatedBy: e.target.value })}
              disabled={maintenanceBusy}
            />

            <textarea
              className="input-glass"
              rows={4}
              placeholder="Reason shown to visitors"
              value={maintenance.message}
              onChange={(e) => setMaintenance({ ...maintenance, message: e.target.value })}
              disabled={maintenanceBusy}
            />

            <div className="glass-card" style={{ padding: 12, margin: '6px 0 2px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <strong>Timer</strong>
                  <span className="meta-text">Auto-end reminder / countdown for visitors</span>
                </div>
                {endsAtLabel ? (
                  <div className="meta-text">Ends at: {endsAtLabel}</div>
                ) : (
                  <div className="meta-text">No timer set</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <input
                  className="input-glass"
                  style={{ width: 140 }}
                  inputMode="numeric"
                  type="number"
                  min={1}
                  step={1}
                  value={timerMinutes}
                  onChange={(e) => setTimerMinutes(e.target.value)}
                  disabled={maintenanceBusy}
                  aria-label="Timer minutes"
                />
                <button
                  className="nav-link"
                  type="button"
                  disabled={maintenanceBusy}
                  onClick={() => setEndsAtFromMinutes(timerMinutes)}
                >
                  Set minutes
                </button>
                <button className="nav-link" type="button" disabled={maintenanceBusy} onClick={() => setEndsAtFromMinutes(15)}>+15m</button>
                <button className="nav-link" type="button" disabled={maintenanceBusy} onClick={() => setEndsAtFromMinutes(60)}>+1h</button>
                <button className="nav-link" type="button" disabled={maintenanceBusy} onClick={clearEndsAt}>Clear</button>
              </div>
              <div className="meta-text" style={{ marginTop: 8 }}>
                When deployed, the server will automatically end maintenance at this time.
              </div>
            </div>

            <button
              className="btn-glass-primary"
              disabled={maintenanceBusy}
              onClick={() => saveMaintenance(maintenance)}
            >
              {maintenanceBusy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
